import React from "react";
import {
    Loader2,
    Rocket,
    CheckCircle2,
    XCircle,
    Archive,
} from "lucide-react";
import type { PreviewStatusConfig } from "../types/preview-environments";

// ─── Status map ──────────────────────────────────────────────────────────────

export const PREVIEW_STATUS_MAP: Record<string, PreviewStatusConfig> = {
    BUILDING: {
        label: "Building",
        color: "#3b82f6",
        bg: "rgba(59, 130, 246, 0.08)",
        border: "rgba(59, 130, 246, 0.2)",
        icon: React.createElement(Loader2, { size: 12, style: { animation: "spin 1s linear infinite" } }),
        pulse: true,
    },
    DEPLOYING: {
        label: "Deploying",
        color: "#8b5cf6",
        bg: "rgba(139, 92, 246, 0.08)",
        border: "rgba(139, 92, 246, 0.2)",
        icon: React.createElement(Rocket, { size: 12 }),
        pulse: true,
    },
    ACTIVE: {
        label: "Active",
        color: "#059669",
        bg: "rgba(5, 150, 105, 0.08)",
        border: "rgba(5, 150, 105, 0.25)",
        icon: React.createElement(CheckCircle2, { size: 12 }),
    },
    FAILED: {
        label: "Failed",
        color: "#ef4444",
        bg: "rgba(239, 68, 68, 0.08)",
        border: "rgba(239, 68, 68, 0.2)",
        icon: React.createElement(XCircle, { size: 12 }),
    },
    DESTROYING: {
        label: "Destroying",
        color: "#f59e0b",
        bg: "rgba(245, 158, 11, 0.08)",
        border: "rgba(245, 158, 11, 0.2)",
        icon: React.createElement(Loader2, { size: 12, style: { animation: "spin 1s linear infinite" } }),
        pulse: true,
    },
    DESTROYED: {
        label: "Destroyed",
        color: "#9ca3af",
        bg: "rgba(156, 163, 175, 0.06)",
        border: "rgba(156, 163, 175, 0.15)",
        icon: React.createElement(Archive, { size: 12 }),
    },
};

// ─── Template variables ──────────────────────────────────────────────────────

export const ALL_TEMPLATE_VARS = [
    "{MIC_PR_NUMBER}",
    "{MIC_BRANCH}",
    "{MIC_TAG_NAME}",
    "{MIC_SHORT_SHA}",
    "{MIC_AUTHOR}",
    "{MIC_NAMESPACE}",
] as const;

/** PR-only vars (hidden when trigger is TAG-only) */
const PR_ONLY_VARS = new Set(["{MIC_PR_NUMBER}"]);
/** TAG-only vars (hidden when trigger is PR-only) */
const TAG_ONLY_VARS = new Set(["{MIC_TAG_NAME}"]);

/** Filter template vars based on active trigger events */
export function getTemplateVarsForTrigger(triggerEvents?: string[]): readonly string[] {
    const events = triggerEvents && triggerEvents.length > 0 ? triggerEvents : ['PR'];
    const hasPR = events.includes('PR');
    const hasTAG = events.includes('TAG');

    return ALL_TEMPLATE_VARS.filter(v => {
        if (!hasPR && PR_ONLY_VARS.has(v)) return false;
        if (!hasTAG && TAG_ONLY_VARS.has(v)) return false;
        return true;
    });
}

/** @deprecated Use ALL_TEMPLATE_VARS with getTemplateVarsForTrigger */
export const PREVIEW_TEMPLATE_VARS = ALL_TEMPLATE_VARS;

/** @deprecated Use ALL_TEMPLATE_VARS with getTemplateVarsForTrigger */
export const APP_TEMPLATE_VARS = ALL_TEMPLATE_VARS;

// ─── Default values ──────────────────────────────────────────────────────────

export const DEFAULT_NAMING_PATTERN = "preview-pr-{MIC_PR_NUMBER}";
export const DEFAULT_TARGET_BRANCH = "main";
export const DEFAULT_MAX_CONCURRENT_PREVIEWS = 5;
export const DEFAULT_TRIGGER_EVENTS = ["PR"];

// ─── Limits ──────────────────────────────────────────────────────────────────

export const MAX_CONCURRENT_PREVIEWS_LIMIT = 20;
export const NAMING_PATTERN_MAX_LENGTH = 100;

// ─── Query / refresh timing ─────────────────────────────────────────────────

export const PREVIEWS_REFRESH_INTERVAL = 30000;
export const PREVIEWS_STALE_TIME = 20000;
export const DEPLOYED_APPS_STALE_TIME = 60000;

// ─── History limits ──────────────────────────────────────────────────────────

export const ACTIVE_HISTORY_LIMIT = 5;
export const PLAYGROUND_HISTORY_LIMIT = 6;

// ─── Panel dimensions ────────────────────────────────────────────────────────

export const PANEL_MAX_WIDTH = 600;
export const PANEL_MIN_WIDTH = 280;
export const PANEL_COLLAPSE_THRESHOLD = 200;
export const PANEL_DEFAULT_WIDTH = 360;

// ─── Draft autosave ──────────────────────────────────────────────────────────

export const DRAFT_AUTOSAVE_DELAY = 30000;
